import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Alert, CircularProgress, Container } from '@mui/material';
import { hasNavPermission } from '../utils/hasPermission';
import useServerPermissions, { mergePermissions } from '../hooks/useServerPermissions';

/**
 * Route-level permission guard.
 *
 * Wraps a child route element and redirects to /dashboard/app when
 * the logged-in user lacks the required permissions.
 *
 * By default uses OR logic (user needs at least ONE of the listed
 * permissions). Pass \`requireAll\` to switch to AND logic (user must
 * have EVERY listed permission).
 *
 * Usage in routes.js:
 *   { path: 'users', element: <PermissionGuard required={[PERMISSIONS.VIEW_USER]}><Users /></PermissionGuard> }
 *   { path: 'admin/tenants', element: <PermissionGuard required={[PERMISSIONS.VIEW_USER, PERMISSIONS.VIEW_ROLE]} requireAll><TenantsAdmin /></PermissionGuard> }
 *
 * @param {{ required: string[], requireAll?: boolean, children: React.ReactNode }} props
 */
export default function PermissionGuard({ required, requireAll = false, serverResolved = false, children }) {
  const { loginUser } = useSelector((state) => state.userSlice);
  const tokenPermissions = loginUser?.decodedToken?.permissions ?? [];
  // serverResolved: also honour company-level overrides resolved by the server
  // (GET /api/authz/effective-permissions). Display only; the server re-checks every call.
  const tokenAllows = required && required.length > 0 && (requireAll
    ? required.every((perm) => tokenPermissions.includes(perm))
    : hasNavPermission(tokenPermissions, required));
  const server = useServerPermissions(serverResolved && !tokenAllows);
  if (serverResolved && !tokenAllows && server.loading) {
    return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Container>;
  }
  const userPermissions = serverResolved ? mergePermissions(tokenPermissions, server.permissions) : tokenPermissions;

  // No permissions required \u2014 render children directly
  if (!required || required.length === 0) return children;

  if (requireAll) {
    // AND logic \u2014 user must have every listed permission
    const hasAll = required.every((perm) => userPermissions.includes(perm));
    if (hasAll) return children;
  } else {
    // OR logic \u2014 user has at least one of the required permissions
    if (hasNavPermission(userPermissions, required)) return children;
  }

  // Deny access \u2014 redirect to dashboard
  return <Navigate to="/dashboard/app" replace />;
}
